import React, { useCallback, useState } from 'react';

import { useInterval } from 'src/common/hooks/useInterval';
import {
  PatientInVisit,
  PatientMissedVisit,
  PatientNotInVisit,
} from 'src/components/pages/pageElements/videoConference/components/VideoStatusPromptCards';
import { useOverrideVisitAutoCancel } from 'src/components/pages/pageElements/videoConference/useOverrideVisitAutoCancel';

export const VideoStatusPromptCard = ({
  startTime,
  isPatientInVisit,
  connected,
  active,
  startCall,
  eventId,
  visitAutoCancelInfo,
}: {
  startTime: number;
  isPatientInVisit: boolean;
  connected: boolean;
  active: boolean;
  startCall: () => void;
  eventId: string;
  visitAutoCancelInfo: {
    autoCancelAt: Date | null;
    autoCanceledAt: Date | null;
    isOverridden: boolean;
    isAutoCancelPrevented: boolean;
    hasSessionOverlapped: boolean;
  };
}) => {
  const { overrideVisitAutoCancel } = useOverrideVisitAutoCancel();

  const [currentTime, setCurrentTime] = useState<number>(0);

  useInterval(() => setCurrentTime(new Date().valueOf()), 1000);

  const handleOverrideAutoCancel = useCallback(async () => {
    await overrideVisitAutoCancel(eventId);
  }, [eventId, overrideVisitAutoCancel]);

  const {
    isOverridden,
    autoCancelAt,
    autoCanceledAt,
    hasSessionOverlapped,
    isAutoCancelPrevented,
  } = visitAutoCancelInfo;

  /** Visit has an auto-cancel time set, the current time is past that time, and it has not yet been auto-canceled */
  const autoCancelInProgress =
    !!autoCancelAt && !autoCanceledAt && currentTime >= autoCancelAt.valueOf();

  return (
    <>
      {isPatientInVisit && connected && !active && !autoCanceledAt && (
        <PatientInVisit onJoinVisit={startCall} />
      )}
      {!isPatientInVisit && !autoCanceledAt && !autoCancelInProgress && (
        <div style={{ zIndex: 4, position: 'absolute' }}>
          <PatientNotInVisit
            isProviderInVisit={active}
            onJoinVisit={startCall}
            isOverridden={isOverridden}
            onOverride={handleOverrideAutoCancel}
            autoCancelAt={autoCancelAt?.valueOf()}
            eventStart={startTime}
            hasSessionOverlapped={hasSessionOverlapped}
            isAutoCancelPrevented={isAutoCancelPrevented}
            currentTime={currentTime}
          />
        </div>
      )}
      {(!!autoCanceledAt || autoCancelInProgress) && !hasSessionOverlapped && (
        <div style={{ zIndex: 4, position: 'absolute' }}>
          <PatientMissedVisit
            onClose={() => window.close()}
            autoCancelInProgress={autoCancelInProgress}
          />
        </div>
      )}
    </>
  );
};
